import { useMemo } from 'react';

import { useGetWizardOverview } from 'lib/hooks/queries/wizard/useGetWizardOverview';

import PHASES_CONFIG from '../../../lib/phasesConfig';

// Destructure phase codes from the single source of truth.
// Order must stay fixed to satisfy React Rules of Hooks.
const [phase0, phase1, phase2, phase3, phase4] = PHASES_CONFIG;

const makeParams = (wizard_instance_id, practiceId, code) => ({
  wizard_instance_id,
  overview_group_code: code,
  wizard_kind_code: 'onboarding',
  practiceId
});

/**
 * Custom hook to fetch all wizard overview stages
 * @param {string} wizard_instance_id - The wizard instance ID
 * @param {number} practiceId - The practice ID
 * @returns {Object} Object containing stagesData, stagesQueries, and isAnyStageLoading
 */
const useGetAllStagesOverview = (wizard_instance_id, practiceId) => {
  const query0 = useGetWizardOverview({
    params: makeParams(wizard_instance_id, practiceId, phase0.code)
  });
  const query1 = useGetWizardOverview({
    params: makeParams(wizard_instance_id, practiceId, phase1.code)
  });
  const query2 = useGetWizardOverview({
    params: makeParams(wizard_instance_id, practiceId, phase2.code)
  });
  const query3 = useGetWizardOverview({
    params: makeParams(wizard_instance_id, practiceId, phase3.code)
  });
  const query4 = useGetWizardOverview({
    params: makeParams(wizard_instance_id, practiceId, phase4.code)
  });

  const stagesQueries = [query0, query1, query2, query3, query4];

  const isAnyStageLoading = useMemo(
    () => stagesQueries.some((query) => query.isLoading),
    [stagesQueries]
  );

  const stagesData = useMemo(
    () => Object.fromEntries(PHASES_CONFIG.map((phase, i) => [phase.code, stagesQueries[i].data])),
    [query0.data, query1.data, query2.data, query3.data, query4.data]
  );

  return {
    stagesData,
    stagesQueries,
    isAnyStageLoading,
    stagesConfig: PHASES_CONFIG,
    invalidateQueryKeys: PHASES_CONFIG.map((phase) => ['wizard-overview', practiceId, phase.code])
  };
};

export default useGetAllStagesOverview;
